import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { Button, useLocalizeMessage, Spinner, Table } from 'libs.nucleus';
import { SectionCard } from 'libs.react';
import { AccessIcfPrompt } from '../components/access_icf_prompt/access_icf_prompt.component';
import { DocumentModal } from '../document_modal';
import { useAccessIcfPrompt } from '../hooks/use_access_icf_prompt.hook';
import { buildDocumentUrl } from '../utils/classic_site_url.helpers';
import { useCreateConsentAction } from './hooks/use_create_consent_action.hook';
import { useParticipantDocuments } from './hooks/use_participant_documents.hook';
import { useParticipantDocumentsTableColumns } from './hooks/use_participant_documents_table_columns.hook';
import { usePtsDocumentAction } from './hooks/use_pts_document_action.hook';
import { useVoidDocumentAction } from './hooks/use_void_document_action.hook';
export const ParticipantDocumentsView = ({ participant, accountId, siteId, apiClient, environment, getApiInstance, getApiErrorMessage, isDocGenEconsentEnabled, isNoPiiSite, isCaregiverEnabled, canCreateDocument, canVoidConsents = false, canOpenDocument, canDownloadConsentPdf = true, requiresIcfAccessWarning = false, formatCaregiverDisplayName, openClassicCreateConsent, accountEmail, accountDisplayName, onOpenDocumentDetails, getDocumentDetailsHref, onDocumentCreated, }) => {
    const translate = useLocalizeMessage();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const { requestOpen, isPromptOpen, onConfirm, onCancel, onClose } = useAccessIcfPrompt(requiresIcfAccessWarning);
    const getDocumentHref = useCallback((document) => buildDocumentUrl(document._id, environment) || '', [environment]);
    const openDocument = useCallback((document) => {
        requestOpen(() => setSelectedDocument(document));
    }, [requestOpen]);
    const closeDocumentModal = useCallback(() => {
        setSelectedDocument(null);
    }, []);
    const { canVoidDocument, openVoidDocument, voidDocumentModal } = useVoidDocumentAction({
        participantId: participant?._id,
        canVoidConsents,
        isDocGenEconsentEnabled,
        apiClient,
        getApiErrorMessage,
    });
    const { isPtsInteractionEnabled, openPtsDocument, ptsDocumentModal } = usePtsDocumentAction({
        apiClient,
        accountId,
        isNoPiiSite,
        isDocGenEconsentEnabled,
        participantId: participant?._id,
        getApiErrorMessage,
        canVoidDocument,
        onVoidDocument: openVoidDocument,
        canDownloadConsentPdf,
    });
    const requestOpenPtsDocument = useCallback((document) => {
        requestOpen(() => openPtsDocument(document));
    }, [openPtsDocument, requestOpen]);
    const { participantDocumentsColumns } = useParticipantDocumentsTableColumns({
        getDocumentHref,
        openDocumentsInModal: isDocGenEconsentEnabled,
        onOpenDocument: openDocument,
        isPtsInteractionEnabled,
        onOpenPtsDocument: requestOpenPtsDocument,
        canOpenDocument,
        requiresIcfAccessWarning,
        canVoidDocument,
        onVoidDocument: openVoidDocument,
        // The details page only exists in the DocGen eConsent Site App, so with the
        // flag off the title link stays on the classic viewer even if a host supplies the route.
        onOpenDocumentDetails: isDocGenEconsentEnabled ? onOpenDocumentDetails : undefined,
        getDocumentDetailsHref,
    });
    const { data: documents, isLoading } = useParticipantDocuments({
        participant,
        accountId,
        siteId,
        apiClient,
        isCaregiverEnabled,
        formatCaregiverDisplayName,
    });
    const { handleCreateConsent, createConsentModal } = useCreateConsentAction({
        participant,
        canCreateDocument,
        isDocGenEconsentEnabled,
        environment,
        siteId,
        accountId,
        getApiInstance,
        getApiErrorMessage,
        openClassicCreateConsent,
        onDocumentCreated,
    });
    if (isLoading || !participant) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    if (isEmpty(documents)) {
        return (_jsxs(_Fragment, { children: [_jsx(SectionCard, { children: _jsxs("section", { className: 'flex flex-col items-center justify-center gap-8 p-10', children: [_jsx("p", { className: 'text-sm text-secondary', children: translate(canOpenDocument === false
                                    ? 'error.participant_documents.unavailable_for_role'
                                    : 'info.participant_documents.no_consents') }), canCreateDocument && (_jsx(Button, { label: translate('action.participant_documents.create_consent'), variant: 'secondary', onClick: handleCreateConsent }))] }) }), createConsentModal, ptsDocumentModal, voidDocumentModal] }));
    }
    return (_jsxs("div", { className: 'flex flex-col gap-4', children: [canCreateDocument && (_jsx("div", { className: 'flex justify-end', children: _jsx(Button, { label: translate('action.participant_documents.create_consent'), variant: 'secondary', onClick: handleCreateConsent }) })), _jsx(Table, { columns: participantDocumentsColumns, data: documents || [], hasPagination: true, initialPageSize: 10, isLoading: isLoading, labelNoResults: translate('label.participant_documents.empty'), paginationNavigationScreenReaderLabel: translate('info.participant_documents.table_navigation') }), createConsentModal, ptsDocumentModal, voidDocumentModal, _jsx(AccessIcfPrompt, { isPromptOpen: isPromptOpen, onConfirm: onConfirm, onCancel: onCancel, onClose: onClose }), isDocGenEconsentEnabled && (_jsx(DocumentModal, { document: selectedDocument, isOpen: !!selectedDocument, onClose: closeDocumentModal, accountId: accountId, accountEmail: accountEmail, accountDisplayName: accountDisplayName, apiClient: apiClient, getApiErrorMessage: getApiErrorMessage, isDocGenEconsentEnabled: true, participantId: participant._id, canVoidDocument: canVoidDocument, classicDocumentUrl: selectedDocument ? getDocumentHref(selectedDocument) || undefined : undefined, onVoidDocument: (document) => {
                    closeDocumentModal();
                    openVoidDocument(document);
                }, canDownloadConsentPdf: canDownloadConsentPdf, isNoPiiSite: isNoPiiSite }))] }));
};
